import type { Reminder, ReminderStatus } from "./types";

/**
 * Presentation rules shared by every surface that shows a reminder (calendar chip,
 * customer sheet), so a "call back" looks the same wherever it appears.
 */

export const REMINDER_CHANNEL_COLORS: Record<
  Reminder["channel"],
  { chip: string; dot: string }
> = {
  // Amber for a voice call back, sky for an SMS — deliberately away from the
  // service colours used by calendar events, so the two never read as the same thing.
  call: {
    chip: "bg-amber-500/15 text-amber-900 dark:text-amber-200 border-amber-500/60",
    dot: "bg-amber-500",
  },
  sms: {
    chip: "bg-sky-500/15 text-sky-900 dark:text-sky-200 border-sky-500/60",
    dot: "bg-sky-500",
  },
};

/**
 * What to show as a follow-up's content.
 *
 * Once the platform has prepared it, `message` is only what the agent *planned* to say
 * during the conversation — a task, often, since the content may not have existed yet.
 * `prepared_message` is what the customer actually received, written against what was true
 * at the moment it fired. Wherever there is one, that is the real answer to "what did we
 * send them?", and `planned` carries the original for the surfaces that show both.
 */
export const reminderContent = (
  reminder: Reminder,
): { text: string; prepared: boolean; planned: string } =>
  reminder.prepared_message
    ? {
        text: reminder.prepared_message,
        prepared: true,
        planned: reminder.message,
      }
    : { text: reminder.message, prepared: false, planned: "" };

export const reminderStatusVariant = (
  status: ReminderStatus,
): "default" | "secondary" | "outline" | "destructive" => {
  switch (status) {
    case "pending":
    case "processing":
      return "default";
    case "sent":
      return "secondary";
    case "failed":
      return "destructive";
    default:
      return "outline";
  }
};

/** Only a reminder that has not started being delivered can still be called off. */
export const isReminderCancellable = (reminder: Reminder) =>
  reminder.status === "pending";

/** Faded on the calendar once it is done or was dropped. */
export const isReminderInactive = (reminder: Reminder) =>
  reminder.status !== "pending" && reminder.status !== "processing";

/** Statuses worth drawing on the calendar — cancelled and skipped ones are noise. */
export const CALENDAR_REMINDER_STATUSES: ReminderStatus[] = [
  "pending",
  "processing",
  "sent",
  "failed",
];
